import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Delete } from "@mui/icons-material";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import { isEmpty, keyBy } from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useEffect, useState } from "react";
import {
  Control,
  FormProvider,
  Path,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { STATES } from "@stw/states";
import {
  AudienceTargetingFieldType,
  AudienceTargetingFilterCategory,
  AudienceTargetingFilterDonationHistoryOperations,
  AudienceTargetingFilterOperation,
  TargetingForm,
  TargetingFormFilter,
  TargetingFormSchema,
} from "@stw/yup-validators";

import {
  AudienceContactFieldType,
  AudienceTargetingFilterCategoryInput,
  CalculateCampaignContactCountMutation,
  CalculateCampaignContactCountMutationVariables,
  CampaignContactCountJob,
  GetAudienceContactFieldsQuery,
  GetAudienceContactFieldsQueryVariables,
  ImportCampaignContactsFromSubscribersMutation,
  ImportCampaignContactsFromSubscribersMutationVariables,
  MessagingStrategyBandwidthShortCodeConfig,
  TrackedJobStatus,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import {
  RhfZipCodeRadius,
  RhfCommaSeparatedTextField,
  RhfCurrencyField,
  RhfCurrencyRange,
  RhfDatePicker,
  RhfNumberRange,
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfNumberField } from "~frontend/components/forms/react-hook-form-components/RhfNumberField";
import { GET_AUDIENCE_CONTACT_FIELDS } from "~frontend/graphql/audience";
import {
  CALCULATE_CAMPAIGN_CONTACT_COUNT_JOB,
  GET_CAMPAIGN,
  SUBSCRIBER_CAMPAIGN_CONTACT_IMPORT,
} from "~frontend/graphql/campaigns";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { generateTargetGroupSentence } from "~frontend/util/generateTargetGroupSentence";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";

// zod enum -> GraphQL enum
function convertFilterCategoryToGraphQL(
  category: AudienceTargetingFilterCategory
): AudienceTargetingFilterCategoryInput {
  switch (category) {
    case AudienceTargetingFilterCategory.Location:
      return AudienceTargetingFilterCategoryInput.Location;
    case AudienceTargetingFilterCategory.DonationHistory:
      return AudienceTargetingFilterCategoryInput.DonationHistory;
    case AudienceTargetingFilterCategory.CustomField:
      return AudienceTargetingFilterCategoryInput.CustomField;
    default:
      throw new Error(`Unknown filter category: ${category}`);
  }
}

// Convert TargetingForm to GraphQL input format
function convertTargetingFormToGraphQL(form: TargetingForm) {
  return {
    targetGroups: form.targetGroups.map(group => ({
      clauses: group.clauses.map(clause => ({
        ...clause,
        filterCategory: convertFilterCategoryToGraphQL(clause.filterCategory),
      })),
      dateFilter: group.dateFilter
        ? {
            key: group.dateFilter.key,
            startDate:
              typeof group.dateFilter.startDate === "string"
                ? group.dateFilter.startDate
                : group.dateFilter.startDate?.toISO(),
            endDate:
              typeof group.dateFilter.endDate === "string"
                ? group.dateFilter.endDate
                : group.dateFilter.endDate?.toISO(),
          }
        : undefined,
    })),
  };
}

type FilterConfig = {
  label: string;
  items: {
    label: string;
    value: AudienceTargetingFilterOperation;
    fieldType: AudienceTargetingFieldType;
    allowDateFiltering?: boolean;
  }[];
};

type FilterHierarchy = {
  [K in AudienceTargetingFilterCategory]: FilterConfig;
};

function customFieldOperations(
  customFields: GetAudienceContactFieldsQuery["getAudienceContactFields"]
) {
  return customFields
    .filter(field => field.isEditable)
    .reduce(
      (acc, field) => {
        switch (field.type) {
          case AudienceContactFieldType.String:
            acc[field.name] = [
              {
                label: "contains text",
                value: "contains",
                fieldType: "TEXT",
              },
              {
                label: "matches",
                value: "matches",
                fieldType: "TEXT",
              },
              {
                label: "does not contain text",
                value: "notContains",
                fieldType: "TEXT",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Number:
            acc[field.name] = [
              {
                label: "is greater than",
                value: "gt",
                fieldType: field.type,
              },
              {
                label: "is less than",
                value: "lt",
                fieldType: field.type,
              },
              {
                label: "is equal to",
                value: "equals",
                fieldType: field.type,
              },
              {
                label: "is not equal to",
                value: "notEquals",
                fieldType: field.type,
              },
              {
                label: "is between",
                value: "between",
                fieldType: "NUMBER_RANGE",
              },
              {
                label: "is not between",
                value: "notBetween",
                fieldType: "NUMBER_RANGE",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Boolean:
            acc[field.name] = [
              {
                label: "is true",
                value: "customTrue",
                fieldType: "BOOLEAN_TRUE",
              },
              {
                label: "is false",
                value: "customFalse",
                fieldType: "BOOLEAN_FALSE",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Enum:
            acc[field.name] = [
              {
                label: "is one of",
                value: "oneOf",
                fieldType: "ENUM",
              },
              {
                label: "is not one of",
                value: "notOneOf",
                fieldType: "ENUM",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.State:
            acc[field.name] = [
              {
                label: "is in state",
                value: "isInCustomState",
                fieldType: "STATE",
              },
              {
                label: "is not in state",
                value: "notInCustomState",
                fieldType: "STATE",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Zip:
            acc[field.name] = [
              {
                label: "is in zip code",
                value: "isInCustomZip",
                fieldType: "ZIP",
              },
              {
                label: "is not in zip code",
                value: "notInCustomZip",
                fieldType: "ZIP",
              },
              {
                label: "is in zip code radius",
                value: "isInCustomZipRadius",
                fieldType: "ZIP_RADIUS",
              },
              {
                label: "is not in zip code radius",
                value: "notInCustomZipRadius",
                fieldType: "ZIP_RADIUS",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          default:
            acc[field.name] = [];
        }
        return acc;
      },
      {} as Record<
        string,
        {
          label: string;
          value: string | number;
          fieldType: string;
          allowDateFiltering?: boolean;
        }[]
      >
    );
}

function availableTargetingFilters({
  includeDonationHistory,
  includeCustomFields,
}: {
  includeDonationHistory: boolean;
  includeCustomFields: boolean;
}): Partial<FilterHierarchy> {
  const FILTER_HIERARCHY: Partial<FilterHierarchy> = {
    location: {
      label: "Location",
      items: [
        {
          label: "is in state",
          value: "isInState",
          fieldType: "STATE",
        },
        {
          label: "is not in state",
          value: "notInState",
          fieldType: "STATE",
        },
        {
          label: "state is empty",
          value: "stateIsNull",
          fieldType: "IS_NULL",
        },
        {
          label: "is in zip code",
          value: "isInZip",
          fieldType: "ZIP",
        },
        {
          label: "is not in zip code",
          value: "notInZip",
          fieldType: "ZIP",
        },
        {
          label: "is in zip code radius",
          value: "isInZipRadius",
          fieldType: "ZIP_RADIUS",
        },
        {
          label: "is not in zip code radius",
          value: "notInZipRadius",
          fieldType: "ZIP_RADIUS",
        },
        {
          label: "zip is empty",
          value: "zipIsNull",
          fieldType: "IS_NULL",
        },
      ],
    },
  };

  if (includeDonationHistory) {
    FILTER_HIERARCHY.donationHistory = {
      label: "Donation History",
      items: [
        {
          label: "total contributions are greater than",
          value: "totalDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "total contributions are less than",
          value: "totalDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "total contributions are between range",
          value: "totalDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "is a recurring donor",
          value: "isRecurringDonor",
          fieldType: "BOOLEAN_TRUE",
        },
        {
          label: "is a recurring donor with recurring amount greater than",
          value: "recurringDonationAmountGt",
          fieldType: "CURRENCY",
        },
        {
          label: "is a recurring donor with recurring amount less than",
          value: "recurringDonationAmountLt",
          fieldType: "CURRENCY",
        },
        {
          label: "is a recurring donor with recurring amount between range",
          value: "recurringDonationAmountBetween",
          fieldType: "CURRENCY_RANGE",
        },
        {
          label: "is not a recurring donor",
          value: "notRecurringDonor",
          fieldType: "BOOLEAN_FALSE",
        },
        {
          label: "has made a donation",
          value: "hasDonated",
          fieldType: "BOOLEAN_TRUE",
          allowDateFiltering: true,
        },
        {
          label: "has not made a donation",
          value: "notHasDonated",
          fieldType: "BOOLEAN_FALSE",
          allowDateFiltering: true,
        },
        {
          label: "has made a donation on a mobile device",
          value: "hasMobileDonation",
          fieldType: "BOOLEAN_TRUE",
          allowDateFiltering: true,
        },
        {
          label: "has not made a donation on a mobile device",
          value: "notHasMobileDonation",
          fieldType: "BOOLEAN_FALSE",
          allowDateFiltering: true,
        },
        {
          label: "last donation was greater than",
          value: "lastDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "last donation was less than",
          value: "lastDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "last donation was between range",
          value: "lastDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "number of donations is greater than",
          value: "numberOfDonationsGt",
          fieldType: "NUMBER",
          allowDateFiltering: true,
        },
        {
          label: "number of donations is less than",
          value: "numberOfDonationsLt",
          fieldType: "NUMBER",
          allowDateFiltering: true,
        },
        {
          label: "number of donations is between range",
          value: "numberOfDonationsBetween",
          fieldType: "NUMBER_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "average donation is greater than",
          value: "averageDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "average donation is less than",
          value: "averageDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "average donation is between range",
          value: "averageDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "highest donation is greater than",
          value: "highestDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "highest donation is less than",
          value: "highestDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "highest donation is between range",
          value: "highestDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
      ],
    };
  }

  if (includeCustomFields) {
    FILTER_HIERARCHY.customField = {
      label: "Custom Field",
      items: [],
    };
  }

  return FILTER_HIERARCHY;
}

const DATE_FILTER_RANGES = [
  { key: "ALL_TIME", label: "All time", start: null, end: null },
  {
    key: "LAST_YEAR",
    label: "Last year",
    start: DateTime.now().minus({ years: 1 }).startOf("year"),
    end: DateTime.now().minus({ years: 1 }).endOf("year"),
  },
  {
    key: "THIS_YEAR",
    label: "This year",
    start: DateTime.now().startOf("year"),
    end: DateTime.now().endOf("day"),
  },
  {
    key: "THIS_MONTH",
    label: "This month",
    start: DateTime.now().startOf("month"),
    end: DateTime.now().endOf("day"),
  },
  {
    key: "LAST_MONTH",
    label: "Last month",
    start: DateTime.now().minus({ months: 1 }).startOf("month"),
    end: DateTime.now().minus({ months: 1 }).endOf("month"),
  },
  {
    key: "CUSTOM_DATE_RANGE",
    label: "Specific date range",
    start: DateTime.now().minus({ days: 1 }).startOf("day"),
    end: DateTime.now().endOf("day"),
  },
];

const DATE_FILTER_RANGE_BY_KEY = keyBy(DATE_FILTER_RANGES, "key");

function TargetingFilter({
  control,
  index,
  targetGroupIndex,
  namePrefix,
  handleRemove,
  audienceFields,
  clause,
  allAvailableTargetingFilters,
  firstDateFilterIndex,
  timezone,
}: {
  control: Control<TargetingForm>;
  index: number;
  targetGroupIndex: number;
  namePrefix: string;
  handleRemove: () => void;
  audienceFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  clause: TargetingFormFilter;
  allAvailableTargetingFilters: Partial<FilterHierarchy>;
  firstDateFilterIndex?: number;
  timezone: string;
}): ReactElement {
  const { setValue, getValues } = useFormContext<TargetingForm>();

  const dateFilterPrefix = `targetGroups.${targetGroupIndex}.dateFilter`;

  const [previousOperation, setPreviousOperation] = useState(
    clause?.filterOperation
  );

  const [previousFieldType, setPreviousFieldType] = useState(clause?.fieldType);

  const filterType: keyof typeof allAvailableTargetingFilters = useWatch({
    control,
    name: `${namePrefix}.${index}.filterCategory` as any,
  });

  const filterOperation = useWatch({
    control,
    name: `${namePrefix}.${index}.filterOperation` as Path<TargetingForm>,
  });

  const customFieldName = useWatch({
    control,
    name: `${namePrefix}.${index}.fieldName` as any,
  });

  const currentDateFilterKey = useWatch({
    control,
    name: `${dateFilterPrefix}.key` as Path<TargetingForm>,
  });

  const currentDateFilterStart = useWatch({
    control,
    name: `${dateFilterPrefix}.startDate` as Path<TargetingForm>,
  });

  const currentDateFilterEnd = useWatch({
    control,
    name: `${dateFilterPrefix}.endDate` as Path<TargetingForm>,
  });

  const availableOperations =
    (filterType === "customField"
      ? customFieldOperations(audienceFields)[customFieldName]
      : allAvailableTargetingFilters[filterType]?.items) || [];

  const selectedOperation = availableOperations.find(
    op => op.value === filterOperation
  );

  useEffect(() => {
    if (!selectedOperation) {
      setValue(
        `${namePrefix}.${index}.filterOperation` as Path<TargetingForm>,
        ""
      );
      setValue(`${namePrefix}.${index}.fieldType` as Path<TargetingForm>, "");
      setValue(`${namePrefix}.${index}.value` as Path<TargetingForm>, "");
      setValue(`${namePrefix}.${index}.values` as Path<TargetingForm>, []);
      return;
    }

    if (selectedOperation.fieldType) {
      if (selectedOperation.fieldType !== previousFieldType) {
        setValue(
          `${namePrefix}.${index}.fieldType` as Path<TargetingForm>,
          selectedOperation.fieldType,
          { shouldDirty: true }
        );
        setPreviousFieldType(
          selectedOperation.fieldType as AudienceTargetingFieldType
        );
      }

      if (selectedOperation.value !== previousOperation) {
        setValue(`${namePrefix}.${index}.value` as Path<TargetingForm>, "", {
          shouldDirty: true,
        });
        setValue(`${namePrefix}.${index}.values` as Path<TargetingForm>, [], {
          shouldDirty: true,
        });
      }

      if (selectedOperation.fieldType.startsWith("BOOLEAN_")) {
        setValue(
          `${namePrefix}.${index}.value` as any,
          selectedOperation.fieldType === "BOOLEAN_TRUE" ? true : false,
          { shouldDirty: true }
        );
      }
    }

    if (selectedOperation.allowDateFiltering && !currentDateFilterKey) {
      setValue(`${dateFilterPrefix}.key` as Path<TargetingForm>, "ALL_TIME", {
        shouldDirty: true,
      });
      setValue(
        `${dateFilterPrefix}.startDate` as Path<TargetingForm>,
        DATE_FILTER_RANGE_BY_KEY["ALL_TIME"].start as null,
        { shouldDirty: true }
      );
      setValue(
        `${dateFilterPrefix}.endDate` as Path<TargetingForm>,
        DATE_FILTER_RANGE_BY_KEY["ALL_TIME"].end as null,
        { shouldDirty: true }
      );
    }

    if (!selectedOperation.allowDateFiltering && !currentDateFilterKey) {
      setValue(dateFilterPrefix as Path<TargetingForm>, null, {
        shouldDirty: true,
      });
    }

    const currentFilterOperation = getValues(
      `${namePrefix}.${index}.filterOperation` as Path<TargetingForm>
    ) as AudienceTargetingFilterOperation;

    setPreviousOperation(currentFilterOperation);
  }, [
    filterOperation,
    setValue,
    namePrefix,
    index,
    getValues,
    previousOperation,
    currentDateFilterKey,
    selectedOperation,
    targetGroupIndex,
    dateFilterPrefix,
    setPreviousOperation,
    previousFieldType,
  ]);

  return (
    <FlexBox direction="column" gap={2}>
      <FlexBox
        direction="row"
        gap={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <FlexBox
          gap={2}
          sx={{
            marginBottom: theme.spacing(1.5),
            "& .MuiInputLabel-root": {
              fontSize: theme.spacing(1.5),
            },
          }}
        >
          <RhfSelect
            name={
              `${namePrefix}.${index}.filterCategory` as Path<TargetingForm>
            }
            label="Filter Type"
            control={control}
            withNoneOption={false}
            sx={{ minWidth: "140px", fontSize: theme.spacing(1.5) }}
            fullWidth
            variant="underlined"
            onChange={val => {
              if (val !== "customField") {
                setValue(
                  `${namePrefix}.${index}.fieldName` as Path<TargetingForm>,
                  "",
                  { shouldDirty: true, shouldValidate: true }
                );
              }
            }}
          >
            {Object.keys(allAvailableTargetingFilters).map(ft => (
              <MenuItem key={ft} value={ft}>
                {
                  allAvailableTargetingFilters[
                    ft as AudienceTargetingFilterCategory
                  ]!.label
                }
              </MenuItem>
            ))}
          </RhfSelect>
          {filterType === "customField" && (
            <RhfSelect
              name={`${namePrefix}.${index}.fieldName` as Path<TargetingForm>}
              label="Custom Field"
              control={control}
              withNoneOption={false}
              sx={{ minWidth: "140px", fontSize: theme.spacing(1.5) }}
              fullWidth
              variant="underlined"
              onChange={newFieldName => {
                const selectedField = audienceFields.find(
                  f => f.name === newFieldName
                );
                if (selectedField) {
                  setValue(
                    `${namePrefix}.${index}.fieldType` as Path<TargetingForm>,
                    selectedField.type
                  );
                }
              }}
            >
              {audienceFields
                .filter(field => field.isEditable)
                .map(field => (
                  <MenuItem key={field.id} value={field.name}>
                    {field.name}
                  </MenuItem>
                ))}
            </RhfSelect>
          )}
          <RhfSelect
            name={
              `${namePrefix}.${index}.filterOperation` as Path<TargetingForm>
            }
            label="Operation"
            control={control}
            withNoneOption={false}
            sx={{ minWidth: "140px", fontSize: theme.spacing(1.5) }}
            fullWidth
            variant="underlined"
            onChange={val => {
              if (val === "isNull" || val === "customIsNull") {
                setValue(
                  `${namePrefix}.${index}.value` as Path<TargetingForm>,
                  "true",
                  { shouldDirty: true, shouldValidate: true }
                );
              }
            }}
          >
            {availableOperations.map(op => (
              <MenuItem key={op.label} value={op.value}>
                {op.label}
              </MenuItem>
            ))}
          </RhfSelect>
          {selectedOperation?.allowDateFiltering && (
            <RhfSelect<TargetingForm>
              name={`${dateFilterPrefix}.key` as Path<TargetingForm>}
              label="Date Range"
              control={control}
              withNoneOption={false}
              sx={{ minWidth: "140px", fontSize: theme.spacing(1.5) }}
              fullWidth
              variant="underlined"
              disabled={
                firstDateFilterIndex !== index &&
                firstDateFilterIndex !== undefined
              }
              onChange={newValue => {
                const { start, end } =
                  DATE_FILTER_RANGE_BY_KEY[newValue as string] || {};
                setValue(
                  `${dateFilterPrefix}.startDate` as Path<TargetingForm>,
                  start?.toISO() ?? null,
                  { shouldDirty: true }
                );
                setValue(
                  `${dateFilterPrefix}.endDate` as Path<TargetingForm>,
                  end?.toISO() ?? null,
                  { shouldDirty: true }
                );
              }}
            >
              {DATE_FILTER_RANGES.map(range => (
                <MenuItem key={range.key} value={range.key}>
                  {range.label}
                </MenuItem>
              ))}
            </RhfSelect>
          )}
        </FlexBox>
        <LinkButton variant="body3" onClick={() => handleRemove()}>
          Remove Filter
        </LinkButton>
      </FlexBox>

      {selectedOperation && (
        <>
          <input
            type="hidden"
            {...control.register(
              `${namePrefix}.${index}.fieldType` as Path<TargetingForm>
            )}
          />
          {selectedOperation?.allowDateFiltering &&
            currentDateFilterKey === "CUSTOM_DATE_RANGE" &&
            currentDateFilterEnd &&
            currentDateFilterStart && (
              <FlexBox direction="row" gap={2}>
                <RhfDatePicker<TargetingForm>
                  name={`${dateFilterPrefix}.startDate` as Path<TargetingForm>}
                  label="Start Date"
                  control={control}
                  fullWidth
                  timezone={timezone}
                  disabled={
                    firstDateFilterIndex !== index &&
                    firstDateFilterIndex !== undefined
                  }
                  maxDate={
                    currentDateFilterEnd
                      ? DateTime.fromJSDate(
                          currentDateFilterEnd as unknown as Date
                        )
                      : undefined
                  }
                />
                <RhfDatePicker<TargetingForm>
                  name={`${dateFilterPrefix}.endDate` as Path<TargetingForm>}
                  label="End Date"
                  control={control}
                  fullWidth
                  timezone={timezone}
                  disabled={
                    firstDateFilterIndex !== index &&
                    firstDateFilterIndex !== undefined
                  }
                  minDate={
                    currentDateFilterStart
                      ? DateTime.fromJSDate(
                          currentDateFilterStart as unknown as Date
                        )
                      : undefined
                  }
                />
              </FlexBox>
            )}
          {selectedOperation.fieldType === "STATE" && (
            <RhfSelect
              label="States"
              fullWidth
              control={control}
              name={`${namePrefix}.${index}.values` as Path<TargetingForm>}
              multiple={true}
              bottomLabel="You may select multiple states."
            >
              {STATES.map(state => (
                <MenuItem key={state.abbreviation} value={state.abbreviation}>
                  {state.name}
                </MenuItem>
              ))}
            </RhfSelect>
          )}
          {selectedOperation.fieldType === "NUMBER" && (
            <RhfNumberField
              label="Amount"
              fullWidth
              control={control}
              name={`${namePrefix}.${index}.value` as Path<TargetingForm>}
              bottomLabel="Enter amount"
            />
          )}
          {selectedOperation.fieldType === "NUMBER_RANGE" && (
            <RhfNumberRange
              control={control}
              name={`${namePrefix}.${index}.values` as Path<TargetingForm>}
              lowLabel="Lower Limit"
              highLabel="Upper Limit"
              fullWidth
            />
          )}
          {selectedOperation.fieldType === "CURRENCY" &&
            filterType === "donationHistory" && (
              <RhfCurrencyField
                label="Amount"
                fullWidth
                control={control}
                name={`${namePrefix}.${index}.value` as Path<TargetingForm>}
                bottomLabel="Enter amount"
              />
            )}
          {selectedOperation.fieldType === "TEXT" && (
            <RhfTextField
              label="Text Value"
              fullWidth
              control={control}
              name={`${namePrefix}.${index}.value` as Path<TargetingForm>}
              bottomLabel="Enter the text to search for"
            />
          )}
          {selectedOperation.fieldType === "ZIP" && (
            <RhfCommaSeparatedTextField
              label="Zip Codes"
              fullWidth
              control={control}
              name={`${namePrefix}.${index}.values` as Path<TargetingForm>}
              placeholder="12345, 67890, 54321"
              bottomLabel="Enter zip codes separated by commas"
            />
          )}
          {selectedOperation.fieldType === "ZIP_RADIUS" && (
            <RhfZipCodeRadius<TargetingForm>
              control={control}
              centerZipsName={
                `${namePrefix}.${index}.filterConfig.centerZips` as Path<TargetingForm>
              }
              radiusName={
                `${namePrefix}.${index}.filterConfig.radius` as Path<TargetingForm>
              }
              valuesName={
                `${namePrefix}.${index}.values` as Path<TargetingForm>
              }
            />
          )}
          {selectedOperation.fieldType === "CURRENCY_RANGE" && (
            <RhfCurrencyRange
              control={control}
              name={`${namePrefix}.${index}.values` as Path<TargetingForm>}
              lowLabel="Lower Limit"
              highLabel="Upper Limit"
              fullWidth
            />
          )}
          {selectedOperation.fieldType.startsWith("BOOLEAN_") && (
            <input
              type="hidden"
              {...control.register(
                `${namePrefix}.${index}.value` as Path<TargetingForm>
              )}
            />
          )}
          {selectedOperation.fieldType === "ENUM" && (
            <RhfSelect
              name={`${namePrefix}.${index}.values` as Path<TargetingForm>}
              label="Value(s)"
              control={control}
              withNoneOption={false}
              sx={{ minWidth: "160px" }}
              fullWidth
              multiple
            >
              {audienceFields
                .find(f => f.name === customFieldName)
                ?.typeParameters?.values?.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
            </RhfSelect>
          )}
        </>
      )}
    </FlexBox>
  );
}

function TargetGroup({
  control,
  index,
  removeHandler,
  audienceFields,
  allAvailableTargetingFilters,
  timezone,
}: {
  control: Control<TargetingForm>;
  index: number;
  removeHandler: () => void;
  audienceFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  allAvailableTargetingFilters: Partial<FilterHierarchy>;
  timezone: string;
}): ReactElement {
  const { setValue } = useFormContext<TargetingForm>();
  const clausesData = useWatch({
    control,
    name: `targetGroups.${index}.clauses`,
  });

  const {
    fields: clauses,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `targetGroups.${index}.clauses`,
  });

  const [targetSentence, setTargetSentence] = useState<string>("");

  useEffect(() => {
    if (clausesData) {
      const sentence = generateTargetGroupSentence(clausesData);
      return sentence.length > 0
        ? setTargetSentence(" - " + sentence)
        : setTargetSentence("");
    }
  }, [clausesData]);

  const defaultFilter = {
    filterCategory: "location",
    filterOperation: "isInState",
    fieldType: "STATE",
    values: [],
  } as TargetingFormFilter;

  const firstDateFilterIndex = clauses.findIndex(
    clause =>
      clause.filterCategory === "donationHistory" &&
      AudienceTargetingFilterDonationHistoryOperations.includes(
        clause.filterOperation as any
      )
  );

  useEffect(() => {
    // If the last date-based filter is removed, clear out the form value
    if (firstDateFilterIndex < 0) {
      setValue(`targetGroups.${index}.dateFilter`, null);
    }
  }, [firstDateFilterIndex, setValue, index]);

  return (
    <FlexBox direction="column">
      <FlexBox
        justifyContent="space-between"
        sx={{ backgroundColor: palette.midGray }}
      >
        <Typography variant="body2" sx={{ padding: theme.spacing(1, 1, 1, 3) }}>
          <strong>Filter Group {index + 1}</strong> - subscribers who meet all
          of the following filters
        </Typography>
        <IconButton onClick={removeHandler} aria-label="delete target">
          <Delete sx={{ height: "16px" }} />
        </IconButton>
      </FlexBox>
      {clauses.map((clause, idx) => (
        <FlexBox
          key={clause.id}
          flexDirection="column"
          sx={{
            backgroundColor: palette.lightGray,
            padding: theme.spacing(1.4, 3),
            marginBottom: "2px",
          }}
        >
          <TargetingFilter
            control={control}
            index={idx}
            targetGroupIndex={index}
            namePrefix={`targetGroups.${index}.clauses`}
            handleRemove={() => remove(idx)}
            audienceFields={audienceFields}
            clause={clause}
            allAvailableTargetingFilters={allAvailableTargetingFilters}
            firstDateFilterIndex={
              firstDateFilterIndex > -1 ? firstDateFilterIndex : undefined
            }
            timezone={timezone}
          />
        </FlexBox>
      ))}

      <FlexBox
        gap={2}
        alignItems="center"
        sx={{
          backgroundColor: palette.lightGray,
          padding: theme.spacing(1.4, 3),
          marginBottom: "2px",
        }}
      >
        <Button
          variant="contained"
          color="tertiary"
          sx={{
            fontSize: theme.spacing(1.5),
            backgroundColor: palette.midGray,
            padding: theme.spacing(0.5, 1.2),
            minWidth: "auto",
          }}
          onClick={() => append(defaultFilter, { shouldFocus: false })}
        >
          + Add Filter
        </Button>
        {targetSentence && (
          <Typography variant="body2">
            <strong>Filter Group {index + 1}</strong> {targetSentence}
          </Typography>
        )}
      </FlexBox>
    </FlexBox>
  );
}

function ContactCountJobResult({
  job,
}: {
  job: Pick<CampaignContactCountJob, "status" | "result" | "errorMessage">;
}): ReactElement {
  if (
    [
      TrackedJobStatus.Running,
      TrackedJobStatus.Queued,
      TrackedJobStatus.Created,
    ].includes(job.status)
  ) {
    return (
      <Typography
        variant="body2"
        sx={{ marginBottom: theme.spacing(2.5) }}
        component={"div"}
      >
        <FlexBox gap={1} alignItems="center">
          <div>Calculating...</div>
          <CircularProgress
            color="inherit"
            size={12}
            sx={{ marginTop: "-1px" }}
          />
        </FlexBox>
      </Typography>
    );
  }

  if (job.result) {
    return (
      <Typography
        variant="body2"
        sx={{ marginBottom: theme.spacing(2.5) }}
        component={"div"}
      >
        <FlexBox gap={1}>
          <strong>
            {plural(job.result.contactCount, "contact", "contacts")} in this
            campaign
          </strong>
          <span>
            Calculated {new Date(job.result.calculatedAt).toLocaleString()}
          </span>
        </FlexBox>
      </Typography>
    );
  }

  if (job.errorMessage) {
    return (
      <Alert severity="error" sx={{ marginBottom: theme.spacing(2.5) }}>
        {job.errorMessage}
      </Alert>
    );
  }

  return <></>;
}

export default function SubscriberTargeting({
  campaign,
  previousJobFilters,
  readOnly,
}: {
  campaign: CampaignForEditor;
  previousJobFilters: TargetingForm | null;
  readOnly?: boolean;
}): ReactElement {
  const subaccountAdminConfig = useSubaccountAdminConfig()!;
  const timezone =
    campaign.textingHoursTimezone || subaccountAdminConfig.textingHoursTimezone;

  const { donationHistoryPageEnabled } = useSubaccount()!;

  const includeDonationHistory = donationHistoryPageEnabled || false;
  const includeCustomFields = useFeatureFlag("audienceCustomFields");
  const allAvailableTargetingFilters = availableTargetingFilters({
    includeDonationHistory,
    includeCustomFields,
  });

  const [filterSubscribers, setFilterSubscribers] =
    useState<boolean>(!!previousJobFilters);

  const [startImport] = useMutation<
    ImportCampaignContactsFromSubscribersMutation,
    ImportCampaignContactsFromSubscribersMutationVariables
  >(SUBSCRIBER_CAMPAIGN_CONTACT_IMPORT, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
    onErrorSnackbarMessage: "Failed to start subscriber import",
  });

  const [calculateCampaignContactCount] = useMutation<
    CalculateCampaignContactCountMutation,
    CalculateCampaignContactCountMutationVariables
  >(CALCULATE_CAMPAIGN_CONTACT_COUNT_JOB, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
    onErrorSnackbarMessage: "Failed to calculate contact count",
    refetchQueries: [GET_CAMPAIGN],
    awaitRefetchQueries: true,
  });

  const defaultTarget = {
    clauses: [
      {
        filterCategory: "location",
        filterOperation: "isInState",
        fieldType: "STATE",
        fieldName: null,
        values: [],
      } as TargetingFormFilter,
    ],
  };

  const defaultTargetGroup = previousJobFilters
    ? TargetingFormSchema.parse(previousJobFilters)
    : { targetGroups: [] };

  const formMethods = useForm<TargetingForm>({
    resolver: zodResolver(TargetingFormSchema),
    mode: "all",
    reValidateMode: "onBlur",
    values: defaultTargetGroup,
  });

  const { control, handleSubmit, formState } = formMethods;

  async function triggerImport(filters: TargetingForm) {
    return startImport({
      variables: {
        input: {
          campaignId: campaign.id,
          filters: convertTargetingFormToGraphQL(filters),
        },
      },
    });
  }

  async function triggerContactCount(filters: TargetingForm) {
    return calculateCampaignContactCount({
      variables: {
        input: {
          campaignId: campaign.id,
          filters: convertTargetingFormToGraphQL(filters),
        },
      },
    });
  }

  const {
    fields: targetGroups,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "targetGroups",
  });

  useEffect(() => {
    if (targetGroups.length === 0) {
      setFilterSubscribers(false);
    }
  }, [targetGroups, setFilterSubscribers]);

  const { subaccount } = useSubaccount()!;

  const {
    data: customFields,
    loading,
    error,
  } = useQuery<
    GetAudienceContactFieldsQuery,
    GetAudienceContactFieldsQueryVariables
  >(GET_AUDIENCE_CONTACT_FIELDS, {
    variables: {
      input: {
        opnId: (
          campaign.messagingStrategy
            .config as MessagingStrategyBandwidthShortCodeConfig
        ).ownedPhoneNumber.id,
        subaccountId: subaccount.id,
      },
    },
    skip: !includeCustomFields,
  });

  return (
    <FormProvider {...formMethods}>
      <form name="subscriberFilterForm">
        {filterSubscribers ? (
          <>
            <Typography variant="body1" sx={{ marginBottom: theme.spacing(2) }}>
              Import short code subscribers who meet the criteria for one or
              more of the following filter groups:
            </Typography>

            <AsyncWrapper loading={loading} errors={error}>
              <FlexBox
                direction="column"
                sx={{ marginBottom: theme.spacing(3) }}
              >
                {targetGroups.map((targetGroup, index) => {
                  return (
                    <div key={targetGroup.id}>
                      {index > 0 && (
                        <Typography
                          variant="body1Bold"
                          sx={{ margin: theme.spacing(2, 0) }}
                          component={"div"}
                        >
                          OR...
                        </Typography>
                      )}
                      <TargetGroup
                        control={control}
                        index={index}
                        removeHandler={() => remove(index)}
                        allAvailableTargetingFilters={
                          allAvailableTargetingFilters
                        }
                        audienceFields={
                          customFields?.getAudienceContactFields || []
                        }
                        timezone={timezone}
                      />
                    </div>
                  );
                })}
              </FlexBox>
            </AsyncWrapper>
            {campaign.contactCountJob && (
              <ContactCountJobResult job={campaign.contactCountJob} />
            )}

            <FlexBox justifyContent="space-between">
              <Button
                color="tertiary"
                variant="contained"
                sx={{ fontSize: theme.spacing(1.5) }}
                onClick={() => append(defaultTarget)}
              >
                + Add Filter Group
              </Button>
              <FlexBox direction="row" gap={1}>
                <Button
                  color="tertiary"
                  variant="contained"
                  onClick={handleSubmit(triggerContactCount)}
                  sx={{ fontSize: theme.spacing(1.5) }}
                  disabled={
                    campaign.contactCountJob?.status ===
                      TrackedJobStatus.Running ||
                    !formState.isValid ||
                    !formState.isDirty
                  }
                >
                  {campaign.contactCountJob
                    ? "Refresh Contact Total"
                    : "Preview Contact Total"}
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit(triggerImport)}
                  disabled={
                    readOnly ||
                    !isEmpty(formState.errors) ||
                    !formState.isDirty ||
                    !formState.isValid
                  }
                  sx={{ fontSize: theme.spacing(1.5) }}
                  data-testid="import-subscribers-button"
                >
                  Import {targetGroups.length > 0 ? "Filtered " : ""} Short Code
                  Subscribers
                </Button>
              </FlexBox>
            </FlexBox>
          </>
        ) : (
          <FlexBox justifyContent="flex-end" gap={1}>
            <Button
              color="tertiary"
              variant="contained"
              sx={{ fontSize: theme.spacing(1.5) }}
              onClick={() => {
                append(defaultTarget);
                setFilterSubscribers(true);
              }}
            >
              Filter Subscribers
            </Button>

            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit(triggerImport)}
              disabled={readOnly}
              sx={{ fontSize: theme.spacing(1.5) }}
              data-testid="import-subscribers-button"
            >
              Import All Short Code Subscribers
            </Button>
          </FlexBox>
        )}
      </form>
    </FormProvider>
  );
}
